import React, { useState } from 'react';
import { Label } from 'flowbite-react';
import { useMutation, useQueryClient } from 'react-query';
import { submitOnlineTest } from 'src/apis/application';
import { useToast } from 'src/context/ToastProvider';
import { UIExtendedApplication, ApplicationRoundStatus } from 'types';

interface Props {
    application: UIExtendedApplication;
}

const ViewOnlineTest = ({ application }: Props) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toast = useToast();
    const queryClient = useQueryClient();

    const ongoingRound = application?.ongoingRound;
    const isOnlineTest = ongoingRound?.type === 'OnlineTest';
    const isCompleted = ongoingRound?.status === ApplicationRoundStatus.Submitted;

    const submitOnlineTestMutation = useMutation(['submitOnlineTest'], submitOnlineTest, {
        onSuccess: () => {
            toast({
                title: 'Online test completed successfully!',
                type: 'success',
            });
            queryClient.refetchQueries(['getAllApplications']);
            setIsSubmitting(false);
        },
        onError: (error: any) => {
            toast({
                title: `Error completing online test: ${error.message}`,
                type: 'error',
            });
            setIsSubmitting(false);
        },
    });

    const handleComplete = () => {
        setIsSubmitting(true);
        submitOnlineTestMutation.mutate({
            applicationId: application._id || '',
            submissionLink: 'completed', // Just mark as completed
        });
    };

    if (!isOnlineTest) {
        return (
            <div className="p-6 text-center">
                <p className="text-gray-500">This is not an online test round.</p>
            </div>
        );
    }

    const onlineTestRemarks = ongoingRound?.onlineTestRemarks?.trim();

    return (
        <div className="p-6">
            <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Online Test - {ongoingRound?.title}
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                    {isCompleted
                        ? 'You have completed this online test successfully.'
                        : 'Please complete the online test and submit your results.'
                    }
                </p>
            </div>

            {ongoingRound?.interviewOrAssignmentLink && (
                <div className="mb-6">
                    <Label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        Online Test Link
                    </Label>
                    <div className="flex flex-wrap gap-3">
                        {isCompleted ? (
                            <span
                                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-gray-500 bg-gray-200 cursor-not-allowed"
                                aria-disabled="true"
                            >
                                Online Test Completed
                            </span>
                        ) : (
                            <a
                                target="_blank"
                                rel="noreferrer"
                                href={ongoingRound.interviewOrAssignmentLink}
                                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg focus:ring-4 text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-300 dark:focus:ring-blue-800"
                            >
                                Take Online Test
                            </a>
                        )}
                        {!isCompleted && (
                            <button
                                type="button"
                                onClick={handleComplete}
                                disabled={isSubmitting}
                                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg focus:ring-4 ${isSubmitting
                                    ? 'text-white bg-blue-400 cursor-not-allowed focus:ring-blue-300 dark:focus:ring-blue-800'
                                    : 'text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-300 dark:focus:ring-blue-800'
                                    }`}
                            >
                                {isSubmitting ? 'Marking as Completed...' : 'Mark as Completed'}
                            </button>
                        )}
                    </div>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        {isCompleted
                            ? 'This online test has been completed successfully.'
                            : 'Click the link above to take your online test. Once completed, come back here and click "Mark as Completed".'
                        }
                    </p>
                </div>
            )}

            {onlineTestRemarks && (
                <div className="mb-6">
                    <Label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Remarks</Label>
                    <p className="text-gray-700 dark:text-gray-200 text-sm leading-6 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 whitespace-pre-line">
                        {onlineTestRemarks}
                    </p>
                </div>
            )}

            {isCompleted && (
                <div className="w-full p-4 bg-green-100 dark:bg-green-900 border border-green-200 dark:border-green-700 rounded-lg">
                    <div className="flex items-center">
                        <svg className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-green-800 dark:text-green-200 font-medium">
                            Online Test Completed Successfully
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewOnlineTest;
